import React from 'react';
import { useTranslation } from 'react-i18next';
import compose from 'lodash/fp/compose';
import { withTranslation, withUserPreferences, withAccount } from 'components/hoc';
import styled from 'styled-components';
import './index.css';
import { AuthGatedTheme } from '../common/AuthGatedTheme';

const UserTermWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 56px 32px 96px;
  box-sizing: border-box;

  @media (max-width: 992px) {
    padding: 48px 24px 80px;
  }

  @media (max-width: 768px) {
    padding: 36px 18px 72px;
  }

  @media (max-width: 540px) {
    padding: 28px 16px 60px;
  }
`;

const Section = styled.section`
  margin-top: 1.5rem;
  line-height: 1.7;

  @media (max-width: 768px) {
    margin-top: 1.25rem;
  }
`;

const StyledList = styled.ul`
  list-style-type: disc;
  padding-left: 2rem;

  li {
    margin-bottom: 0.75rem;
    line-height: 1.6;
  }
`;

const TermsContent = ({ isAuth, translate, handleDisabledClick }) => {
  const textColor = isAuth ? { color: 'var(--admincat-color-grey-2)' } : {};
  const bgColor = isAuth ? { backgroundColor: 'var(--admincat-color-grey-4)' } : {};

  return (
    <div className="credits-disclaimer" style={bgColor}>
      <div className="disclaimer">
        <h1 style={textColor}>{translate('Terms and Conditions')}</h1>

        <Section>
          <h3 style={textColor}>
            1. {translate('Scope and changes to these terms of use')}
          </h3>
          <br />
          <p className="userterms-content" style={textColor}>
            1.1.{' '}
            {translate(
              `These terms of use regulate the service utilization based relationship between the customer and AdminCat GmbH (hereinafter "AdminCat" or "we"), Landgraf-Karl-Str. 21A, 34131 Kassel, Germany (E-mail:`,
            )}
            <a style={{ fontSize: '18px' }} href="mailto:admin@admincat.net">
              {translate('admin@admincat.net')}
            </a>
            ){' '}
            {translate(
              `as a provider of E-mail, data and document storage services.`,
            )}
            <br />
            <br />
            1.2.{' '}
            {translate(
              'AdminCat reserves the right to change these terms of use as well as the service description and prices. AdminCat shall notify the customer of any changes. If changes are made to the detriment of the customer, the customer may terminate the user relationship without notice and close his account. AdminCat shall inform the Customer of this right of termination in the notification of change.',
            )}
          </p>
        </Section>

        <Section>
          <h3 style={textColor}>2. {translate('Usage relationship')}</h3>
          <br />
          <p className="userterms-content" style={textColor}>
            2.1.{' '}
            {translate(
              'AdminCat is an online platform that provides E-mail, document storage and data processing services. The contract for the use of AdminCat is established by an order from the customer in the form of an online registration, for which only an existing e-mail address is required.',
            )}
            <br />
            <br />
            2.2.{' '}
            {translate(
              'After the free trial period of at least one month, the user can decide whether they wish to continue using AdminCat by paying a monthly or annual subscription fee for the service. If the user decides not to subscribe during the trial period, they will no longer have access to Spaces, but will be listed as an inactive Space member as long as other paying members are assigned to that Space. The user can still take out a subscription and actively use AdminCat again at a later date. However, if an inactive user is no longer assigned to a Space, they will be automatically deleted after a grace period of three months following the end of the last payment period.',
            )}
          </p>
        </Section>

        <Section>
          <h3 style={textColor}>3. {translate('Services')}</h3>
          <br />
          <p className="userterms-content" style={textColor}>
            3.1.{' '}
            {translate(
              'During (and also after) registration, the customer can create a Space that includes access to an e-mail inbox of the same name. The creator of a Space automatically becomes its first administrator. A user can belong to several Spaces as a member, but can only manage one Space and therefore cannot create another if they already manage one.',
            )}
            <br />
            <br />
            {translate(
              'Alternatively, a user can join another existing Space during (and also after) registration, provided that the administrator of this Space approves this. To do this, an approval request is sent to the administrator by email.',
            )}
            <br />
            <br />
            {translate(
              'After the establishment of a Space or the admission to another, the following services are available to the customer',
            )}
            :
            <br />
            <br />
            <StyledList style={textColor}>
              <li>
                {translate(
                  `One or, if several users have access, a common space e-mail address in the format`,
                )}{' '}
                <a
                  style={{ fontSize: '18px' }}
                  href="mailto:desiredname@admincat.net"
                >
                  {translate('desiredname@admincat.net')}
                </a>
              </li>
              <li>
                {translate(
                  'You can only manage one Space (including e-mail address) in total',
                )}
              </li>
              <li>
                {translate(
                  'Joining Spaces (with the approval of the administrators) is possible indefinitely',
                )}
              </li>
              <li>
                {translate(
                  'Sending emails is limited to 600 emails/month to prevent misuse; the receipt of emails, on the other hand, is not limited',
                )}
              </li>
              <li>
                {translate(
                  'Each Space offers 2 GB storage capacity for messages, data and documents',
                )}
              </li>
              <li>
                {translate(
                  'Sending emails with documents with a file size of up to 10 MB and a total email size of up to 25 MB',
                )}
              </li>
              <li>
                {translate(
                  'Upload single documents up to 25 MB in size and multiple documents up to 50 MB in size at once',
                )}
              </li>
              <li>
                {translate(
                  'Suggest up to 5 new entries per month in various preset selection lists, e.g. for organizations or document types on the platform',
                )}
              </li>
              <li>
                {translate(
                  'Spam filter: emails suspected of being spam are not accepted by our system (reject)',
                )}
              </li>
              <li>
                {translate(
                  'Virus filter: incoming e-mails containing a virus are deleted and not delivered to the customer',
                )}
              </li>
            </StyledList>
            <br />
            {translate(
              'If the storage space of a Space is full, no more messages can be received or documents uploaded. On the Space dashboard, users can check the amount of storage space used for each Space. An extension is currently not possible. In this case, users are therefore asked to release storage space that is no longer required.',
            )}
            <br />
            <br />
            3.2.{' '}
            {translate(
              'AdminCat is usually available 24 hours a day. However, in cases of force majeure we cannot guarantee the uninterrupted accessibility and availability of all services. Of course we will announce planned maintenance work related downtimes via e-mail and on our website in advance.',
            )}
            <br />
            <br />
            3.3.{' '}
            {translate(
              'As part of the provision of its services, AdminCat further reserves the right to inform the customer at irregular intervals (not in the form of a newsletter) by email about important developments and special offers. By accepting these GTCs, the customer agrees to this procedure.',
            )}
          </p>
        </Section>

        <Section>
          <h3 style={textColor}>4. {translate('Usage fee')}</h3>
          <br />
          <p className="userterms-content" style={textColor}>
            4.1.{' '}
            {translate(
              `AdminCat can be used free of charge and without service restrictions during the test phase of at least 1 month. After that, a monthly or annual subscription can be selected in the user settings (go to the`,
            )}
            <span
              style={{
                fontSize: '18px',
                color: 'inherit',
                textDecoration: 'none',
                cursor: 'default',
              }}
              onClick={handleDisabledClick}
            >
              {translate('subscription page')}
            </span>
            {translate(`to see the current subscription fees).`)}
          </p>
        </Section>

        <Section>
          <h3 style={textColor}>5. {translate('Obligations of the customer')}</h3>
          <br />
          <p className="userterms-content" style={textColor}>
            5.1.{' '}
            {translate(
              'The customer will keep the access password to his account secret and change it immediately or have it changed by AdminCat if unauthorized third parties could have gained knowledge of it.',
            )}
            <br />
            <br />
            5.2.{' '}
            {translate(
              'The customer is also liable for third parties who use or have used services via his account, either authorized or unauthorized. This does not apply if the customer is not responsible for unauthorized use. The customer must provide evidence that he is not responsible for such use.',
            )}
            <br />
            <br />
            5.3.{' '}
            {translate(
              'The customer will not send any advertising by e-mail for commercial purposes via the mail service and will not use the mail service to send standardized e-mails to a large number of recipients.',
            )}
          </p>
        </Section>

        <Section>
          <h3 style={textColor}>6. {translate('Abuse and protection of minors')}</h3>
          <br />
          <p className="userterms-content" style={textColor}>
            6.1. {translate('The customer undertakes not to misuse AdminCat')}:
            <br />
            <br />
            <StyledList style={textColor}>
              <li>
                {translate('not to send spam (no more than 600 e-mails per month)')}
              </li>
              <li>
                {translate(
                  'not to violate any laws (e.g. sending forbidden or harassing messages)',
                )}
              </li>
              <li>
                {translate(
                  'not to violate regulations for the protection of young people',
                )}
              </li>
              <li>
                {translate(
                  'not to use any mechanisms, software or scripts in connection with the use of the services that could impair or disrupt the functioning of AdminCat',
                )}
              </li>
            </StyledList>
            <br />
            6.2.{' '}
            {translate(
              'The customer is liable to AdminCat for damage caused by violating his obligations under 6.1., and releases AdminCat from any third party claims in this regard. This does not apply, if he is not responsible for the violation. The customer must provide evidence that he is not responsible for the violation.',
            )}
            <br />
            <br />
            6.3.{' '}
            {translate(
              'If the customer uses AdminCat contrary to the provisions of 6.1 and 5.3, the user account can get blocked by AdminCat without prior notice. The right to terminate the contract without notice remains unaffected.',
            )}
          </p>
        </Section>

        <Section>
          <h3 style={textColor}>7. {translate('Privacy')}</h3>
          <br />
          <p className="userterms-content" style={textColor}>
            7.1.{' '}
            {translate(
              'AdminCat and the customer will comply with the statutory data protection requirements, in particular according to the Telemedia Act (TMG) and the Federal Data Protection Act (BDSG). Inventory and usage data of the customer are only collected, processed or used insofar as this is necessary for the execution of the contractual relationship.',
            )}
            <br />
            <br />
            7.2.{' '}
            {translate(
              'In order to ensure the service integrity and compliance with the Terms of Use of AdminCat, in cases of reasonable suspicion of misuse AdminCat is entitled to determine polluters and restrict the usage of services for these customers.',
            )}
            <br />
            <br />
            7.3.{' '}
            {translate(
              'Further information on the type, scope and purpose of the collection, processing and use of personal and other data can be found in the Privacy policy of AdminCat.',
            )}
          </p>
        </Section>

        <Section>
          <h3 style={textColor}>8. {translate('Warranty and Liability')}</h3>
          <br />
          <p className="userterms-content" style={textColor}>
            8.1.{' '}
            {translate(
              "AdminCat encrypts and protects the managed data and documents on the live server as far as this is possible with the current state of the art. With regular backups, we also prevent major data loss from occurring. Development guidelines and internal processes make sure, that new features are thoroughly tested in a test environment and the users data isn't get compromised by any updates. For the unlikely event of data loss however, we recommend users to keep digital copies on their local systems as a precaution. Export functions for emails and documents are provided for that purpose.",
            )}
            <br />
            <br />
            8.2.{' '}
            {translate(
              'AdminCat does not guarantee that all of the services mentioned under item 3 are available and free of errors at all times. This applies in particular if access to these services is caused by disruptions that are beyond the control of AdminCat. We will immediately eliminate disruptions to the services mentioned under item 3 within the scope of the existing technical and operational possibilities.',
            )}
            <br />
            <br />
            8.3.{' '}
            {translate(
              'Any liability claim against us related to a possible data loss suffered or incorrect and/or harmful data processing needs to be documented or reasonably reproducible. Our support team is your first level contact, ready to analyze incidents, give advice or support with quick bug-fixing in case we identify design and processing errors in our responsibility.',
            )}
          </p>
        </Section>

        <Section>
          <h3 style={textColor}>
            9. {translate('Termination, deletion of the account')}
          </h3>
          <br />
          <p className="userterms-content" style={textColor}>
            9.1.{' '}
            {translate(
              "The usage relationship can run for an indefinite period in principle, as long as the user subscribed and paid for the service in advance. It can actively get terminated by the customer at any time – not via email, but by deleting the user account directly under the user settings, in which case we unfortunately can't provide a reimbursement for the remaining prepaid subscription. The right to terminate the contract without notice in accordance with section 1.2 or for an important reason (sections 5 and 6) remains unaffected.",
            )}
            <br />
            <br />
            9.2.{' '}
            {translate(
              'A deletion removes all sensitive details of a user, such as email, password, its Space memberships and payments. However, for technical reasons, user id and the freely defined user name will remain in the system. Inactive user accounts, whose subscription ended more than three months ago and which are also no longer members of a Space anymore will get deleted automatically by AdminCat.',
            )}
            <br />
            <br />
            9.3.{' '}
            {translate(
              'If the subscription of a Space admin expires, the admin deletes themselves or is unable to continue in the admin role for other reasons without a new admin being appointed, another member of the Space can request the assignment of the admin role in the settings. If the previous admin does not respond within 3 weeks, the admin role is automatically assigned to the applicant so that the space continues to have an admin.',
            )}
            <br />
            <br />
            9.4.{' '}
            {translate(
              'If there are no remaining Space members (e.g. due to deletion) or the paid use of all relevant inactive users of a Space ended more than 3 months ago, the Space and all associated data will be automatically deleted accordingly.',
            )}
          </p>
        </Section>

        <Section>
          <h3 style={textColor}>10. {translate('Governing Law')}</h3>
          <br />
          <p className="userterms-content" style={textColor}>
            {translate(
              'The law of the Federal Republic of Germany applies exclusively to all legal relationships between AdminCat and the customer. The law on the United Nations Convention on the International Sale of Goods ("CISG") does not apply.',
            )}
          </p>
          <br />
          <br />
          <p className="userterms-content" style={textColor}>
            {translate('Status: April 2025')}
          </p>
        </Section>
      </div>
    </div>
  );
};

const UserTerms = (props) => {
  const {
    tr,
    account: {
      user: { isAuth },
    },
  } = props;
  const { t } = useTranslation();

  React.useEffect(() => {
    window.scroll(0, 0);
  }, []);

  const handleDisabledClick = (e) => {
    e.preventDefault();
    alert('Subscription page is temporarily disabled');
  };

  // Use tr for authenticated users, t for non-authenticated users
  const translate = isAuth ? tr : t;

  return (
    <AuthGatedTheme isAuth={isAuth}>
      <UserTermWrapper>
        <TermsContent
          isAuth={isAuth}
          translate={translate}
          handleDisabledClick={handleDisabledClick}
        />
      </UserTermWrapper>
    </AuthGatedTheme>
  );
};

export default compose(withTranslation, withUserPreferences, withAccount)(UserTerms);
